import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { AxiosError, AxiosInstance } from 'axios'
import { md5 } from 'js-md5'
import { useToast } from 'vue-toastification'
import type { SetUser, User, UserNotifications } from '~/interfaces/users'
import MyToast from '~/components/MyToast.vue'
import type { GetBasketResponse } from '~/composables/api/basket'
import { ref } from 'vue'

type GetResponse = User

//const userToken = ref()
export const useUserCredentials = () => {

  const cookie = useCookie<{
    accessToken: string
    refreshToken: string
    isAuthenticated: boolean
  }>('user_credentials', {
    sameSite: 'lax',
    secure: process.env.NODE_ENV === 'production',
    httpOnly: false,
    watch: true,
    //domain: "new.vkusilosos.ru",
   // path: "/",
    maxAge: 180 * 24 * 60 * 60,
    default: () => ({
      accessToken: '',
      refreshToken: '',
      isAuthenticated: false,
    }),
  })

 /* if(!userToken.value) {
    userToken.value = cookie.value
  }*/

  const reset = () => {
    cookie.value = null // - not working on staging
    document.cookie = 'user_credentials=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
  }

  return { userCredentials: cookie, resetUserCredentials: reset }
}

export const useSignOut = () => {
  const { resetUserCredentials } = useUserCredentials()
  const queryClient = useQueryClient()
  const privateAxios = usePrivateAxiosInstance() // - is undefined!!!
  const { mutate } = useMutation({
    mutationFn: async () => {
      //privateAxios - is undefined!!!
      const response = await useNuxtApp().$axiosPrivate.get('auth/logout')

      //const response = await privateAxios.get('auth/logout')
      return response
    },
    onSuccess: () => {
      resetUserCredentials()
      queryClient.clear()
      queryClient.removeQueries()
      location.reload()
    },
  })

  return mutate
}

export const useDeleteAccount = () => {
  const { resetUserCredentials } = useUserCredentials()
  const queryClient = useQueryClient()
  const privateAxios = usePrivateAxiosInstance()

  const { mutate } = useMutation({
    mutationFn: async () => {
      const response = await privateAxios.delete('user/me')
      return response
    },
    onSuccess: () => {
      resetUserCredentials()
      queryClient.clear()
      queryClient.removeQueries()
      location.reload()
    },
  })

  return mutate
}

export const useUserQueryFn = async (privateAxios: AxiosInstance) => {
  const response = await privateAxios.get<GetResponse>('user/me')
  return response.data
}

export const useUser = <SData>(select: (response: GetResponse) => SData) => {
  const privateAxios = usePrivateAxiosInstance()
  const { userCredentials } = useUserCredentials()

  return useQuery({
    queryKey: ['user'],
    queryFn: () => useUserQueryFn(privateAxios),
    select,
    enabled: computed(() => userCredentials.value.isAuthenticated),
  })
}


export const useSetUserNotifications = () => {
  const privateAxios = usePrivateAxiosInstance()
  const invalidate = useInvalidateUser()

  return useMutation({
    mutationFn: async (data: UserNotifications) => {
      const response = await privateAxios.post('user/notifications', data)
      return response.data
    },
    onSuccess: invalidate,
  })
}

export const useSetUser = () => {
  const privateAxios = usePrivateAxiosInstance()
  const invalidate = useInvalidateUser()

  return useMutation({
    mutationFn: async (data: SetUser) => {
      if (data.last_password) {
        data.last_password = md5(data.last_password)
      }
      if (data.new_password) {
        data.new_password = md5(data.new_password)
      }
      const response = await privateAxios.post('user/me', data)
      return response.data
    },
    onSuccess: invalidate,
  })
}

export const useSendRecovery = () => {
  const publicAxios = usePublicAxiosInstance()

  return useMutation({
    mutationFn: async (vals: { email: string }) => {
      const response = await publicAxios.post('auth/forgot', {
        email: vals.email,
      })
      return response.data
    },
  })
}

export const useSendReview = () => {
  const privateAxios = usePrivateAxiosInstance()

  return useMutation({
    mutationFn: async (vals: any) => {
      const response = await privateAxios.post('api/message', vals)
      return response.data
    },
  })
}

export const useTelegramSendContactRequest = () => {
  const privateAxios = usePrivateAxiosInstance()

  return useMutation({
    mutationFn: async (vals: any) => {
      const response = await privateAxios.post('auth/telegram/send-contact-request', vals)
      return response.data
    },
  })
}

export const useTelegramIsContactProvided = () => {
  const privateAxios = usePrivateAxiosInstance()

  return useMutation({
    mutationFn: async (vals: any) => {
      const response = await privateAxios.post('auth/telegram/is-contact-provided', vals)
      return response.data
    },
  })
}


export const useTelegramIsContactProvidedChecker = (data: any, enabled: boolean) => {
  return useQuery({
    queryKey: ['telegramContactProvided'],
    queryFn: async () => {
      const privateAxios = usePrivateAxiosInstance()

      const response = await privateAxios.post('auth/telegram/is-contact-provided', data)

      if (response.data.is_contact_provided && response.data.token.length > 0) {
        return response.data
      } else {
        throw("Repeat")
      }
    },
    retry: true,
    retryDelay: 1000,
    enabled: enabled
  })
}

interface UseSendOtpVals {
  phone: string
}
export const useSendOtp = (config: { onCheckCode?: (v: UseSendOtpVals, data: string) => void }) => {
  const privateAxios = usePrivateAxiosInstance()
  const toast = useToast()

  return useMutation({
    mutationFn: (vals: UseSendOtpVals) => {
      return privateAxios.post<{ action: string, data: string }>('auth/login/new', vals).then((r) => r.data)
    },

    onSuccess(data, vals) {
      if (data.action === 'check code') {
        if (config.onCheckCode) config.onCheckCode(vals, data.data)
      }
    },

    onError(error: AxiosError<{ action: string, code: number }>) {
      if (error.response?.status === 408) {
        toast({
          component: MyToast,
          props: {
            title: 'Вы уже есть в системе',
            detail: 'Пожалуйста, авторизуйтесь',
          },
        })
      }

      if (error.response?.data.action === 'many times' || error.response?.data.code === 3 ) {
        toast({
          component: MyToast,
          props: {
            title: 'Слишком много попыток',
            detail: 'Попробуйте позже',
          },
        })
      }
    },
  })
}

export const useInvalidateUser = () => {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({
      queryKey: ['user'],
    })
  }
}

export const useFCMToken = () => {
  return useState<string | undefined>('fbToken')
}
